import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Share2, Trophy, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SharedPlaylistCard } from "@/components/music/SharedPlaylistCard";
import { MusicActivityFeed } from "@/components/music/MusicActivityFeed";
import { SyncCompetitionCard } from "@/components/music/SyncCompetitionCard";
import { CollaborativePlaylistEditor } from "@/components/music/CollaborativePlaylistEditor";
import { useSharedPlaylists } from "@/hooks/useSharedPlaylists";
import { useMusicActivity } from "@/hooks/useMusicActivity";
import { useSyncCompetition } from "@/hooks/useSyncCompetition";
import { useCollaborativePlaylist } from "@/hooks/useCollaborativePlaylist";
import { toast } from "sonner";

export default function MusicSocial() {
  const navigate = useNavigate();
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);

  const {
    playlists: sharedPlaylists,
    isLoading: loadingShared,
    fetchPlaylists: fetchShared,
    likePlaylist,
  } = useSharedPlaylists();

  const { activities, isLoading: loadingActivities } = useMusicActivity();

  const {
    competitions,
    participants,
    isLoading: loadingCompetitions,
    fetchCompetitions,
    fetchParticipants,
    joinCompetition,
  } = useSyncCompetition();

  const {
    playlists: collaborativePlaylists,
    tracks,
    isLoading: loadingCollaborative,
    fetchPlaylists: fetchCollaborative,
    fetchTracks,
    addTrack,
    voteTrack,
  } = useCollaborativePlaylist();

  useEffect(() => {
    fetchShared();
    fetchCompetitions();
    fetchCollaborative();
  }, [fetchShared, fetchCompetitions, fetchCollaborative]);

  useEffect(() => {
    if (selectedPlaylistId) {
      fetchTracks(selectedPlaylistId);
    }
  }, [selectedPlaylistId, fetchTracks]);

  useEffect(() => {
    if (competitions.length > 0) {
      fetchParticipants(competitions[0].id);
    }
  }, [competitions, fetchParticipants]);

  const handleShare = (shareCode: string) => {
    const shareUrl = `${window.location.origin}/music-social?code=${shareCode}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copiado para clipboard!');
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="shrink-0"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Música Social</h1>
              <p className="text-sm text-muted-foreground">
                Compartilhe e descubra músicas com a comunidade
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="shared" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="shared" className="gap-2">
              <Share2 className="w-4 h-4" />
              Playlists
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2">
              <Users className="w-4 h-4" />
              Atividade
            </TabsTrigger>
            <TabsTrigger value="competitions" className="gap-2">
              <Trophy className="w-4 h-4" />
              Competições
            </TabsTrigger>
            <TabsTrigger value="collaborative" className="gap-2">
              <Users className="w-4 h-4" />
              Colaborativas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="shared" className="space-y-4">
            {loadingShared ? (
              <p className="text-center text-muted-foreground">Carregando...</p>
            ) : (
              sharedPlaylists.map((playlist) => (
                <SharedPlaylistCard
                  key={playlist.id}
                  playlist={playlist}
                  onLike={likePlaylist}
                  onShare={handleShare}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="activity">
            <MusicActivityFeed activities={activities} isLoading={loadingActivities} />
          </TabsContent>

          <TabsContent value="competitions" className="space-y-4">
            {loadingCompetitions ? (
              <p className="text-center text-muted-foreground">Carregando...</p>
            ) : (
              competitions.map((competition) => (
                <SyncCompetitionCard
                  key={competition.id}
                  competition={competition}
                  participantsCount={
                    participants.filter((p) => p.competition_id === competition.id).length
                  }
                  onJoin={joinCompetition}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="collaborative" className="space-y-4">
            {loadingCollaborative ? (
              <p className="text-center text-muted-foreground">Carregando...</p>
            ) : collaborativePlaylists.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto mb-4 opacity-50 text-muted-foreground" />
                <p className="text-muted-foreground">
                  Nenhuma playlist colaborativa ainda
                </p>
              </div>
            ) : (
              collaborativePlaylists.map((playlist) => (
                <CollaborativePlaylistEditor
                  key={playlist.id}
                  playlistId={playlist.id}
                  playlistName={playlist.name}
                  tracks={
                    selectedPlaylistId === playlist.id
                      ? tracks
                      : []
                  }
                  onAddTrack={addTrack}
                  onVote={voteTrack}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
